import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { AuthProvider } from './context/AuthContext';
import { ThemeContextProvider } from './context/ThemeContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import StudentShell from './pages/student/components/StudentShell';
import TeacherShell from './pages/teacher/components/TeacherShell';
import RingManager from './components/RingManager';
import ChunkErrorBoundary from './components/ChunkErrorBoundary';

// Login Components
import AdminLogin from './pages/login/AdminLogin';
import TeacherLogin from './pages/login/TeacherLogin';
import StudentLogin from './pages/login/StudentLogin';

// Teacher Components
// Only the dashboard stays eager - it is the authenticated landing route for
// this portal, so lazy-loading it would put a chunk round trip in front of the
// first screen a teacher ever sees. Every other teacher page is lazy below.
import TeacherDashboard from './pages/teacher/Dashboard';

// Student Components
// Same rule as the teacher portal: the dashboard is the authenticated landing
// route, so it stays eager. Every other student page is lazy below.
import StudentDashboard from './pages/student/Dashboard';

// Admin Components - route-level code splitting (first slice).
//
// Each admin page sits behind React.lazy, so its module - and the libraries
// only it reaches - land in their own chunk instead of the initial bundle.
// That is what keeps @mui/x-date-pickers and @tiptap/prosemirror, which
// nothing outside the Admin portal imports, out of the Teacher and Student
// builds entirely.
//
// Import paths and component names are unchanged and the routes below are
// untouched; only the moment a module is fetched moves. The remaining teacher
// and student pages stay eager for now - they are a later slice.
const AdminDashboard = React.lazy(() => import('./pages/admin/Dashboard'));
const AdminStudents = React.lazy(() => import('./pages/admin/Students'));
const AdminTeachers = React.lazy(() => import('./pages/admin/Teachers'));
const AdminClasses = React.lazy(() => import('./pages/admin/Classes'));
const AdminReports = React.lazy(() => import('./pages/admin/Reports'));
const AdminChat = React.lazy(() => import('./pages/admin/ChatManagement'));
const AdminCalendar = React.lazy(() => import('./pages/admin/Calendar'));
const AdminFinance = React.lazy(() => import('./pages/admin/Finance'));
const AdminTrialRequests = React.lazy(() => import('./pages/admin/TrialRequests'));
const AdminRescheduleRequests = React.lazy(() => import('./pages/admin/RescheduleRequests'));
const AdminGroupClasses = React.lazy(() => import('./pages/admin/GroupClasses'));
const AdminGroupClassScheduler = React.lazy(() => import('./pages/admin/GroupClassScheduler'));
const ProfileManagement = React.lazy(() => import('./pages/admin/ProfileManagement'));
const StudentCreate = React.lazy(() => import('./pages/admin/StudentCreate'));
const StudentEdit = React.lazy(() => import('./pages/admin/StudentEdit'));
const TeacherCreate = React.lazy(() => import('./pages/admin/TeacherCreate'));
const TeacherEdit = React.lazy(() => import('./pages/admin/TeacherEdit'));
const AdminSettings = React.lazy(() => import('./pages/admin/Settings'));

// Student Invoices, lazy for the same reason but on its own: it is the only
// eager route that reaches jspdf (and with it jspdf-autotable, pako, fflate,
// fast-png, iobuffer and EnhancedInvoiceView) - roughly 1.3 MB of source that
// every portal was shipping in its initial bundle for a page most visitors
// never open. The route, its guard and the Suspense boundary are unchanged.
const StudentInvoices = React.lazy(() => import('./pages/student/Invoices'));

// Teacher and Student Chat, lazy as a pair: they are the last two eager routes
// reaching components/SafeHtml and therefore dompurify, which only leaves the
// initial bundle once BOTH of them do. Neither is the landing route for its
// portal. Routes, guards, shells and the Suspense boundary are unchanged, and
// Chat.js itself is untouched.
const TeacherChat = React.lazy(() => import('./pages/teacher/Chat'));
const StudentChat = React.lazy(() => import('./pages/student/Chat'));

// The remaining Teacher pages. TeacherShell and TeacherDashboard stay eager
// above: the shell wraps every teacher route, and the dashboard is the
// authenticated landing route. Import paths, component names, routes, guards,
// shells and the Suspense boundary are all unchanged - only the moment each
// module is fetched moves.
const TeacherClasses = React.lazy(() => import('./pages/teacher/Classes'));
const TeacherStudents = React.lazy(() => import('./pages/teacher/Students'));
const TeacherHomework = React.lazy(() => import('./pages/teacher/Homework'));
const TeacherAttendance = React.lazy(() => import('./pages/teacher/Attendance'));
const TeacherCalendar = React.lazy(() => import('./pages/teacher/Calendar'));
const TeacherReports = React.lazy(() => import('./pages/teacher/Reports'));
const TeacherQuickTest = React.lazy(() => import('./pages/teacher/QuickTest'));
const TeacherProfile = React.lazy(() => import('./pages/teacher/Profile'));
const TeacherSettings = React.lazy(() => import('./pages/teacher/Settings'));

// The remaining Student pages, on the same terms as the teacher ones above:
// StudentShell and StudentDashboard stay eager, everything else is lazy.
// `Settings` is pages/Settings.js and backs /student/settings, which is why it
// sits with the student group rather than the admin one.
const StudentClasses = React.lazy(() => import('./pages/student/Classes'));
const StudentCalendar = React.lazy(() => import('./pages/student/Calendar'));
const StudentTeachers = React.lazy(() => import('./pages/student/Teachers'));
const StudentHomework = React.lazy(() => import('./pages/student/Homework'));
const StudentAttendance = React.lazy(() => import('./pages/student/Attendance'));
const StudentQuickTest = React.lazy(() => import('./pages/student/QuickTest'));
const Settings = React.lazy(() => import('./pages/Settings'));

function App() {
  const portalType = process.env.REACT_APP_PORTAL_TYPE || 'admin';
  
  // Determine default dashboard based on portal type
  const getDefaultDashboard = () => {
    switch(portalType) {
      case 'teacher':
        return '/teacher/dashboard';
      case 'student':
        return '/student/dashboard';
      default:
        return '/admin/dashboard';
    }
  };
  
  const defaultDashboard = getDefaultDashboard();
  
  return (
    <ThemeContextProvider>
      <AuthProvider>
        <Router>
          <RingManager />
          {/* One boundary for every lazily loaded route. It sits inside the
              Router but AFTER RingManager and outside its subtree, so a route
              chunk still in flight can never unmount the ring listeners. The
              fallback mirrors the loading UI ProtectedRoute already shows. */}
          <ChunkErrorBoundary>
          <Suspense fallback={(
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
              <CircularProgress />
            </Box>
          )}>
            <Routes>
            {/* PRODUCTION MODE: Proper login routing */}
            <Route path="/login" element={
              portalType === 'teacher' ? <TeacherLogin /> :
              portalType === 'student' ? <StudentLogin /> :
              <AdminLogin />
            } />
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/teacher/login" element={<TeacherLogin />} />
            <Route path="/student/login" element={<StudentLogin />} />
            
            {/* Admin Routes - Protected */}
            <Route path="/admin/dashboard" element={
              <ProtectedRoute requiredRole="admin">
                <Layout><AdminDashboard /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/dashboard" element={
              <ProtectedRoute requiredRole={portalType}>
                {portalType === 'teacher' ? <TeacherShell><TeacherDashboard /></TeacherShell> :
                portalType === 'student' ? <StudentShell><StudentDashboard /></StudentShell> :
                <Layout><AdminDashboard /></Layout>}
              </ProtectedRoute>
            } />
            
            <Route path="/admin/students" element={
              <ProtectedRoute requiredRole="admin">
                <Layout><AdminStudents /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/teachers" element={
              <ProtectedRoute requiredRole="admin">
                <Layout><AdminTeachers /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/classes" element={
              <ProtectedRoute requiredRole="admin">
                <Layout><AdminClasses /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/reports" element={
              <ProtectedRoute requiredRole="admin">
                <Layout><AdminReports /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/chats" element={
              <ProtectedRoute requiredRole="admin">
                <Layout><AdminChat /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/calendar" element={
              <ProtectedRoute requiredRole="admin">
                <Layout><AdminCalendar /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/finance" element={
              <ProtectedRoute requiredRole="admin">
                <Layout><AdminFinance /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/trial-requests" element={
              <ProtectedRoute requiredRole="admin">
                <Layout><AdminTrialRequests /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/group-classes" element={
              <ProtectedRoute requiredRole="admin">
                <Layout><AdminGroupClasses /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/group-classes/:id/schedule" element={
              <ProtectedRoute requiredRole="admin">
                <Layout><AdminGroupClassScheduler /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/reschedule-requests" element={
              <ProtectedRoute requiredRole="admin">
                <AdminRescheduleRequests />
              </ProtectedRoute>
            } />
            <Route path="/admin/profile" element={
              <ProtectedRoute requiredRole="admin">
                <Layout><ProfileManagement /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/students/create" element={
              <ProtectedRoute requiredRole="admin">
                <Layout><StudentCreate /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/students/edit/:id" element={
              <ProtectedRoute requiredRole="admin">
                <Layout><StudentEdit /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/teachers/create" element={
              <ProtectedRoute requiredRole="admin">
                <Layout><TeacherCreate /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/teachers/edit/:id" element={
              <ProtectedRoute requiredRole="admin">
                <Layout><TeacherEdit /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/settings" element={
              <ProtectedRoute requiredRole="admin">
                <Layout><AdminSettings /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/teacher/settings" element={
              <ProtectedRoute requiredRole="teacher">
                <TeacherShell><TeacherSettings /></TeacherShell>
              </ProtectedRoute>
            } />
            <Route path="/student/settings" element={
              <ProtectedRoute requiredRole="student">
                <StudentShell><Settings /></StudentShell>
              </ProtectedRoute>
            } />
            
            {/* Teacher Routes */}
            <Route path="/teacher/dashboard" element={
              <ProtectedRoute requiredRole="teacher">
                <TeacherShell><TeacherDashboard /></TeacherShell>
              </ProtectedRoute>
            } />
            <Route path="/teacher/classes" element={
              <ProtectedRoute requiredRole="teacher">
                <TeacherShell><TeacherClasses /></TeacherShell>
              </ProtectedRoute>
            } />
            <Route path="/teacher/students" element={
              <ProtectedRoute requiredRole="teacher">
                <TeacherShell><TeacherStudents /></TeacherShell>
              </ProtectedRoute>
            } />
            <Route path="/teacher/homework" element={
              <ProtectedRoute requiredRole="teacher">
                <TeacherShell><TeacherHomework /></TeacherShell>
              </ProtectedRoute>
            } />
            <Route path="/teacher/attendance" element={
              <ProtectedRoute requiredRole="teacher">
                <TeacherShell><TeacherAttendance /></TeacherShell>
              </ProtectedRoute>
            } />
            <Route path="/teacher/calendar" element={
              <ProtectedRoute requiredRole="teacher">
                <TeacherShell><TeacherCalendar /></TeacherShell>
              </ProtectedRoute>
            } />
            <Route path="/teacher/chat" element={
              <ProtectedRoute requiredRole="teacher">
                <TeacherShell><TeacherChat /></TeacherShell>
              </ProtectedRoute>
            } />
            <Route path="/teacher/reports" element={
              <ProtectedRoute requiredRole="teacher">
                <TeacherShell><TeacherReports /></TeacherShell>
              </ProtectedRoute>
            } />
            <Route path="/teacher/quick-test" element={
              <ProtectedRoute requiredRole="teacher">
                <TeacherShell><TeacherQuickTest /></TeacherShell>
              </ProtectedRoute>
            } />
            <Route path="/teacher/profile" element={
              <ProtectedRoute requiredRole="teacher">
                <TeacherShell><TeacherProfile /></TeacherShell>
              </ProtectedRoute>
            } />
            
            {/* Student Routes */}
            <Route path="/student/dashboard" element={
              <ProtectedRoute requiredRole="student">
                <StudentShell><StudentDashboard /></StudentShell>
              </ProtectedRoute>
            } />
            <Route path="/student/classes" element={
              <ProtectedRoute requiredRole="student">
                <StudentShell><StudentClasses /></StudentShell>
              </ProtectedRoute>
            } />
            <Route path="/student/calendar" element={
              <ProtectedRoute requiredRole="student">
                <StudentShell><StudentCalendar /></StudentShell>
              </ProtectedRoute>
            } />
            <Route path="/student/teachers" element={
              <ProtectedRoute requiredRole="student">
                <StudentShell><StudentTeachers /></StudentShell>
              </ProtectedRoute>
            } />
            <Route path="/student/homework" element={
              <ProtectedRoute requiredRole="student">
                <StudentShell><StudentHomework /></StudentShell>
              </ProtectedRoute>
            } />
            <Route path="/student/invoices" element={
              <ProtectedRoute requiredRole="student">
                <StudentShell><StudentInvoices /></StudentShell>
              </ProtectedRoute>
            } />
            <Route path="/student/chat" element={
              <ProtectedRoute requiredRole="student">
                <StudentShell><StudentChat /></StudentShell>
              </ProtectedRoute>
            } />
            <Route path="/student/attendance" element={
              <ProtectedRoute requiredRole="student">
                <StudentShell><StudentAttendance /></StudentShell>
              </ProtectedRoute>
            } />
            <Route path="/student/quick-test" element={
              <ProtectedRoute requiredRole="student">
                <StudentShell><StudentQuickTest /></StudentShell>
              </ProtectedRoute>
            } />
            </Routes>
          </Suspense>
          </ChunkErrorBoundary>
        </Router>
      </AuthProvider>
    </ThemeContextProvider>
  );
}

export default App;